import React, { useState, useEffect } from "react";
import { useToast } from "@/shared/components/ui/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/shared/components/ui/sheet";
import {
  User, Key, Settings, Copy, Eye, EyeOff, CheckCircle2, Save, Info, Loader2, Route,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import * as engineService from "@/shared/api/services/engineService";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const PROVIDERS = [
  { code: "SCB", name: "Siam Commercial Bank" },
  { code: "KBANK", name: "Kasikorn Bank" },
  { code: "BBL", name: "Bangkok Bank" },
  { code: "KTB", name: "Krungthai Bank" },
  { code: "TRUEWALLET", name: "TrueMoney Wallet" },
  { code: "PROMPTPAY", name: "PromptPay QR" },
  { code: "LINEPAY", name: "LINE Pay" },
  { code: "SHOPEEPAY", name: "ShopeePay" },
];

function generateApiKey(prefix = "rsl_pk_") {
  return prefix + Array.from({ length: 32 }, () =>
    "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[Math.floor(Math.random() * 62)]
  ).join("");
}

/* ─── Always-Dark Input ────────────────────────────────────────
   Uses WebkitBoxShadow inset trick to override browser autofill
   white background. Works for email, password, text, number.
─────────────────────────────────────────────────────────────── */
function DarkInput({ value, onChange, type = "text", placeholder, disabled, accent = "#22d3ee", suffix, autoComplete = "off", error }) {
  const [focused, setFocused] = useState(false);

  const baseBg = disabled ? "rgba(2,6,23,0.35)" : "rgba(2,6,23,0.78)";
  const borderColor = error ? "rgba(239, 68, 68, 0.8)" : (focused ? `${accent}40` : "rgba(148,163,184,0.1)");
  const shadow = focused ? `0 0 0 3px ${error ? "rgba(239, 68, 68, 0.12)" : `${accent}12`}` : "none";
  /* The webkit inset shadow overrides autofill background */
  const webkitShadow = `0 0 0px 1000px ${baseBg} inset, ${shadow}`;

  return (
    <div className="relative">
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        style={{
          width: "100%",
          padding: suffix ? "9px 40px 9px 12px" : "9px 12px",
          borderRadius: 10,
          fontSize: 13,
          background: baseBg,
          border: `1px solid ${borderColor}`,
          color: disabled ? "#475569" : "#e2e8f0",
          /* Force text colour on autofill */
          WebkitTextFillColor: disabled ? "#475569" : "#e2e8f0",
          /* Kill autofill white bg */
          WebkitBoxShadow: webkitShadow,
          caretColor: "#e2e8f0",
          outline: "none",
          boxShadow: shadow,
          transition: "border-color 0.2s, box-shadow 0.2s",
          opacity: disabled ? 0.6 : 1,
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {suffix && <div className="absolute right-3 top-1/2 -translate-y-1/2">{suffix}</div>}
    </div>
  );
}

/* ─── Dark Textarea ─────────────────────────────────────────── */
function DarkTextarea({ value, onChange, rows = 2, disabled }) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea value={value} onChange={onChange} rows={rows} disabled={disabled}
      style={{
        width: "100%", padding: "9px 12px", borderRadius: 10, fontSize: 13, resize: "vertical",
        background: disabled ? "rgba(2,6,23,0.35)" : "rgba(2,6,23,0.78)",
        border: `1px solid ${focused ? "rgba(34,211,238,0.35)" : "rgba(148,163,184,0.1)"}`,
        color: disabled ? "#475569" : "#e2e8f0", outline: "none",
        boxShadow: focused ? "0 0 0 3px rgba(34,211,238,0.07)" : "none",
        transition: "border-color 0.2s, box-shadow 0.2s",
        opacity: disabled ? 0.6 : 1,
      }}
      onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
    />
  );
}

/* ─── Dark Select ───────────────────────────────────────────── */
function DarkSelectField({ value, onChange, children, accent = "#22d3ee", disabled }) {
  const [focused, setFocused] = useState(false);
  return (
    <div className="h-10 flex items-center px-3 rounded-xl" style={{
      background: disabled ? "rgba(2,6,23,0.35)" : "rgba(2,6,23,0.78)",
      border: `1px solid ${focused ? `${accent}35` : "rgba(148,163,184,0.1)"}`,
      boxShadow: focused ? `0 0 0 3px ${accent}0d` : "none",
      transition: "border-color 0.2s, box-shadow 0.2s",
      opacity: disabled ? 0.6 : 1,
    }}>
      <select value={value} onChange={onChange} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} disabled={disabled}
        className="outline-none text-slate-300 text-sm w-full cursor-pointer" style={{ background: "transparent" }}>
        {children}
      </select>
    </div>
  );
}

/* ─── Label ─────────────────────────────────────────────────── */
function FL({ children }) {
  return <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">{children}</label>;
}

/* ─── Tab Button ────────────────────────────────────────────── */
function TabBtn({ active, onClick, icon: Icon, label }) {
  return (
    <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={onClick}
      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold cursor-pointer outline-none border-0"
      style={{
        background: active ? "linear-gradient(135deg, rgba(34,211,238,0.15), rgba(34,211,238,0.07))" : "transparent",
        border: active ? "1px solid rgba(34,211,238,0.25)" : "1px solid transparent",
        color: active ? "#22d3ee" : "#64748b",
        transition: "all 0.2s",
      }}>
      <Icon className="h-3.5 w-3.5" />{label}
    </motion.button>
  );
}

/* ─── Custom Toggle ─────────────────────────────────────────── */
function DarkToggle({ checked, onCheckedChange, disabled }) {
  return (
    <motion.div whileTap={disabled ? {} : { scale: 0.95 }} onClick={() => !disabled && onCheckedChange(!checked)}
      style={{
        width: 42, height: 23, borderRadius: 12, cursor: disabled ? "not-allowed" : "pointer",
        background: checked ? "linear-gradient(135deg, #22d3ee, #0284c7)" : "rgba(34,211,238,0.08)",
        border: `1px solid ${checked ? "transparent" : "rgba(34,211,238,0.15)"}`,
        boxShadow: checked ? "0 4px 12px rgba(2,132,199,0.3)" : "none",
        position: "relative",
        opacity: disabled ? 0.6 : 1,
        transition: "all 0.3s",
        flexShrink: 0,
      }}>
      <motion.div animate={{ left: checked ? 20 : 2 }} transition={{ type: "spring", stiffness: 380, damping: 24 }}
        style={{ position: "absolute", top: 2, width: 17, height: 17, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.3)" }} />
    </motion.div>
  );
}

export default function EngineResellerForm({ open, onClose, reseller, onSave, saving, readOnly }) {
  const { t, language } = useLanguage();
  const isEdit = !!reseller;
  const [activeTab, setActiveTab] = useState("general");
  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(null);
  const [errors, setErrors] = useState({});
  const { toast } = useToast();

  const { data: engineProfile } = useQuery({
    queryKey: ["engine-profile"],
    queryFn: () => engineService.getProfile(),
    enabled: open,
  });

  const { data: fullReseller, isLoading: isLoadingDetails } = useQuery({
    queryKey: ["engine-reseller", reseller?.id],
    queryFn: () => engineService.getResellerById(reseller.id),
    enabled: !!(open && reseller?.id),
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    status: "ACTIVE",
    code: "",
    api_key: generateApiKey("rsl_pk_"),
    secret_key: generateApiKey("rsl_sk_"),
    deposit_fee: 0,
    withdrawal_fee: 0,
    max_merchants: 10,
    allowed_providers: [],
    routing_override: { failover_enabled: true, priority_providers: [] },
    permissions: { can_create_merchants: true, can_edit_fees: false, can_view_webhook_logs: true, can_regenerate_api_keys: true },
    contact_name: "",
    contact_phone: "",
    support_email: "",
    notes: "",
  });

  useEffect(() => {
    setErrors({});
    const baseReseller = fullReseller || reseller;
    if (baseReseller) {
      setFormData(prev => ({
        ...prev,
        name: baseReseller.name || "",
        email: baseReseller.email || "",
        password: "",
        status: baseReseller.status || "ACTIVE",
        code: baseReseller.code || "",
        api_key: baseReseller.apiKey || prev.api_key,
        secret_key: baseReseller.secretKey || prev.secret_key,
        deposit_fee: baseReseller.depositFee || 0,
        withdrawal_fee: baseReseller.withdrawalFee || 0,
        max_merchants: baseReseller.maxMerchants || 10,
        allowed_providers: baseReseller.allowedProviders || [],
        routing_override: baseReseller.routingOverride || { failover_enabled: true, priority_providers: [] },
        contact_name: baseReseller.contactName || "",
        contact_phone: baseReseller.contactPhone || "",
        support_email: baseReseller.supportEmail || "",
        notes: baseReseller.notes || "",
      }));
    } else {
      setFormData({
        name: "",
        email: "",
        password: "",
        status: "ACTIVE",
        code: "",
        api_key: generateApiKey("rsl_pk_"),
        secret_key: generateApiKey("rsl_sk_"),
        deposit_fee: 0,
        withdrawal_fee: 0,
        max_merchants: 10,
        allowed_providers: [],
        routing_override: { failover_enabled: true, priority_providers: [] },
        permissions: { can_create_merchants: true, can_edit_fees: false, can_view_webhook_logs: true, can_regenerate_api_keys: true },
        contact_name: "",
        contact_phone: "",
        support_email: "",
        notes: "",
      });
    }
    setActiveTab("general");
  }, [reseller, fullReseller, open, engineProfile]);

  const set = (field, value) => {
    if (readOnly) return;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleSubmit = () => {
    if (readOnly) return;
    const newErrors = {};
    if (!formData.name) newErrors.name = true;
    if (!isEdit) {
      if (!formData.email) newErrors.email = true;
      if (!formData.password) newErrors.password = true;
      if (!formData.code) newErrors.code = true;
      if (!formData.api_key) newErrors.api_key = true;
      if (!formData.secret_key) newErrors.secret_key = true;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast({
        variant: "destructive",
        title: t("validationError") || "Validation Error",
        description: t("pleaseFillRequiredFields") || "Please fill all required fields.",
      });
      return;
    }

    if (reseller) onSave({
      name: formData.name,
      status: formData.status,
      email: formData.email,
      code: formData.code,
      apiKey: formData.api_key,
      secretKey: formData.secret_key,
      contactName: formData.contact_name,
      contactPhone: formData.contact_phone,
      supportEmail: formData.support_email,
      notes: formData.notes,
      withdrawalFee: formData.withdrawal_fee,
      depositFee: formData.deposit_fee,
      maxMerchants: formData.max_merchants,
      allowedProviders: formData.allowed_providers,
      routingOverride: formData.routing_override,
    });
    else onSave({
      email: formData.email,
      password: formData.password,
      name: formData.name,
      code: formData.code,
      apiKey: formData.api_key,
      secretKey: formData.secret_key,
      contactName: formData.contact_name,
      contactPhone: formData.contact_phone,
      supportEmail: formData.support_email,
      notes: formData.notes,
      withdrawalFee: formData.withdrawal_fee,
      depositFee: formData.deposit_fee,
      maxMerchants: formData.max_merchants,
      allowedProviders: formData.allowed_providers,
      routingOverride: formData.routing_override,
    });
  };

  const tabs = [
    { id: "general", icon: User, label: t("generalTab") || "General" },
    { id: "fees", icon: Settings, label: t("feesLimitsTab") || "Fees" },
    { id: "providers", icon: Route, label: "Providers" },
    { id: "api", icon: Key, label: t("apiKeys") || "API Keys" },
  ];

  return (
    <Sheet open={open} onOpenChange={onClose}>
      {/* Inject global style to forcefully override autofill in this sheet */}
      <style>{`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0px 1000px rgba(2,6,23,0.78) inset !important;
          -webkit-text-fill-color: #e2e8f0 !important;
          caret-color: #e2e8f0 !important;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>

      <SheetContent
        className="w-full sm:max-w-xl overflow-y-auto"
        style={{ background: "rgba(2,6,23,0.98)", borderLeft: "1px solid rgba(34,211,238,0.14)" }}
      >
        <div className="absolute top-0 left-0 right-0 h-px"
          style={{ background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.4), rgba(139,92,246,0.3), transparent)" }} />

        <SheetHeader className="pb-4" style={{ borderBottom: "1px solid rgba(148,163,184,0.08)" }}>
          <SheetTitle className="text-slate-100">
            {readOnly ? (t("viewReseller") || "View Reseller") : isEdit ? (t("editReseller") || "Edit Reseller") : (t("createNewReseller") || "Create New Reseller")}
          </SheetTitle>
          <SheetDescription className="text-slate-500">
            {readOnly ? (t("viewingPartnerDetails") || "Viewing partner details") : isEdit ? (t("updateResellerDetails") || "Update reseller details") : (t("setUpNewResellerPartner") || "Set up a new reseller partner")}
          </SheetDescription>
        </SheetHeader>

        {/* Tab Bar */}
        <div className="flex gap-1 mt-5 mb-5 p-1 rounded-xl"
          style={{ background: "rgba(2,6,23,0.6)", border: "1px solid rgba(148,163,184,0.08)" }}>
          {tabs.map(tab => <TabBtn key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)} icon={tab.icon} label={tab.label} />)}
        </div>

        <AnimatePresence mode="wait">
          {/* ── General Tab ── */}
          {activeTab === "general" && (
            <motion.div key="general" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.18 }} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FL>{t("businessNameRequired") || "Business Name *"}</FL>
                  <DarkInput value={formData.name} onChange={e => { set("name", e.target.value); setErrors(prev => ({ ...prev, name: false })); }} placeholder={t("businessNamePlaceholder") || "Enter name"} error={errors.name} disabled={readOnly} />
                </div>
                <div>
                  <FL>{t("resellerCode") || "Reseller Code"} *</FL>
                  <DarkInput value={formData.code} onChange={e => { set("code", e.target.value.toUpperCase()); setErrors(prev => ({ ...prev, code: false })); }} placeholder="RSL001" disabled={isEdit} error={errors.code} />
                </div>
              </div>

              <div>
                <FL>{t("loginEmailRequired") || "Login Email *"}</FL>
                {/* autoComplete="new-password" tricks browsers into not autofilling */}
                <DarkInput
                  type="email"
                  value={formData.email}
                  onChange={e => { set("email", e.target.value); setErrors(prev => ({ ...prev, email: false })); }}
                  placeholder={t("loginEmailPlaceholder") || "admin@example.com"}
                  disabled={isEdit || readOnly}
                  autoComplete="new-password"
                  error={errors.email}
                />
                {isEdit && <p className="text-[10px] text-slate-600 mt-1">{t("emailCannotBeChangedAfterCreation") || "Email cannot be changed"}</p>}
              </div>

              {!readOnly && (
                <div>
                  <FL>{isEdit ? t("newPasswordLeaveBlank") || "New Password (leave blank to keep)" : t("passwordRequired") || "Password *"}</FL>
                  <DarkInput
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={e => { set("password", e.target.value); setErrors(prev => ({ ...prev, password: false })); }}
                    placeholder={isEdit ? t("enterNewPassword") || "Enter new password" : t("enterPassword") || "Enter password"}
                    autoComplete="new-password"
                    error={errors.password}
                    disabled={readOnly}
                    suffix={
                      <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} type="button" onClick={() => setShowPassword(v => !v)} className="outline-none cursor-pointer">
                        {showPassword ? <EyeOff className="h-4 w-4 text-slate-500" /> : <Eye className="h-4 w-4 text-slate-500" />}
                      </motion.button>
                    }
                  />
                </div>
              )}

              {isEdit && (
                <div>
                  <FL>{t("status") || "Status"}</FL>
                  <DarkSelectField value={formData.status} onChange={e => set("status", e.target.value)} disabled={readOnly}>
                    <option value="ACTIVE" style={{ background: "#020617" }}>{t("active") || "Active"}</option>
                    <option value="INACTIVE" style={{ background: "#020617" }}>{t("inactive") || "Inactive"}</option>
                    <option value="SUSPENDED" style={{ background: "#020617" }}>{t("suspended") || "Suspended"}</option>
                  </DarkSelectField>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FL>{t("contactName") || "Contact Name"}</FL>
                  <DarkInput value={formData.contact_name} onChange={e => set("contact_name", e.target.value)} disabled={readOnly} />
                </div>
                <div>
                  <FL>{t("contactPhone") || "Contact Phone"}</FL>
                  <DarkInput value={formData.contact_phone} onChange={e => set("contact_phone", e.target.value)} disabled={readOnly} />
                </div>
              </div>

              <div>
                <FL>{t("supportEmail") || "Support Email"}</FL>
                <DarkInput type="email" value={formData.support_email} onChange={e => set("support_email", e.target.value)} autoComplete="new-password" disabled={readOnly} />
              </div>

              <div>
                <FL>{t("notes") || "Notes"}</FL>
                <DarkTextarea value={formData.notes} onChange={e => set("notes", e.target.value)} rows={2} disabled={readOnly} />
              </div>
            </motion.div>
          )}

          {/* ── Fees Tab ── */}
          {activeTab === "fees" && (
            <motion.div key="fees" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.18 }} className="space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: t("depositFeeThb") || "Deposit Fee (THB)", key: "deposit_fee" },
                  { label: t("withdrawalFeeThb") || "Withdrawal Fee (THB)", key: "withdrawal_fee" },
                ].map(f => (
                  <div key={f.key}>
                    <FL>{f.label}</FL>
                    <DarkInput type="number" value={formData[f.key]} onChange={e => set(f.key, parseFloat(e.target.value) || 0)} disabled={readOnly} />
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FL>{t("maxMerchantsLabel") || "Max Merchants"}</FL>
                  <DarkInput type="number" value={formData.max_merchants} onChange={e => set("max_merchants", parseInt(e.target.value) || 10)} disabled={readOnly} />
                </div>
              </div>
            </motion.div>
          )}

          {/* ── Providers Tab ── */}
          {activeTab === "providers" && (
            <motion.div key="providers" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.18 }} className="space-y-6">
              {/* Allowed Providers Section */}
              <div className="p-4 rounded-2xl" style={{ background: "rgba(2,6,23,0.6)", border: "1px solid rgba(148,163,184,0.08)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-1 h-3.5 bg-cyan-400 rounded-full" />
                  <FL>Allowed Providers</FL>
                </div>
                <p className="text-[11px] text-slate-500 mb-4">Select which payment providers this reseller can use</p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {PROVIDERS.map(p => {
                    const isSelected = formData.allowed_providers?.includes(p.code);
                    return (
                      <motion.div
                        key={p.code}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          if (readOnly) return;
                          const cur = formData.allowed_providers || [];
                          const next = cur.includes(p.code) ? cur.filter(x => x !== p.code) : [...cur, p.code];
                          set("allowed_providers", next);
                          // Also remove from priority if removed from allowed
                          if (!next.includes(p.code)) {
                            const nextPri = (formData.routing_override?.priority_providers || []).filter(x => x !== p.code);
                            set("routing_override", { ...formData.routing_override, priority_providers: nextPri });
                          }
                        }}
                        className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
                        style={{
                          background: isSelected ? "rgba(34,211,238,0.08)" : "rgba(2,6,23,0.4)",
                          borderColor: isSelected ? "rgba(34,211,238,0.3)" : "rgba(148,163,184,0.1)",
                        }}
                      >
                        <div className="flex-shrink-0">
                          <div className={`w-4 h-4 rounded-md border flex items-center justify-center transition-colors ${isSelected ? 'bg-cyan-500 border-cyan-500' : 'border-slate-700'}`}>
                            {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                          </div>
                        </div>
                        <span className={`text-[12px] font-medium truncate ${isSelected ? 'text-cyan-400' : 'text-slate-400'}`}>
                          {p.name}
                        </span>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Routing Override Section */}
              <div className="p-4 rounded-2xl" style={{ background: "rgba(2,6,23,0.6)", border: "1px solid rgba(148,163,184,0.08)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-1 h-3.5 bg-cyan-400 rounded-full" />
                  <FL>Routing Override</FL>
                </div>
                <p className="text-[11px] text-slate-500 mb-4">Configure routing behavior for this reseller</p>

                <div className="p-4 rounded-xl mb-4" style={{ background: "rgba(2,6,23,0.8)", border: "1px solid rgba(34,211,238,0.1)" }}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[13px] font-bold text-slate-200">Failover Enabled</p>
                      <p className="text-[11px] text-slate-500">Automatically try next provider on failure</p>
                    </div>
                     <DarkToggle
                      checked={formData.routing_override?.failover_enabled}
                      onCheckedChange={v => set("routing_override", { ...formData.routing_override, failover_enabled: v })}
                      disabled={readOnly}
                    />
                  </div>
                </div>

                {formData.allowed_providers?.length > 0 && (
                  <div className="mt-4">
                    <FL>Priority Providers (In Order)</FL>
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      {PROVIDERS.filter(p => formData.allowed_providers.includes(p.code)).map(p => {
                        const isPrimary = formData.routing_override?.priority_providers?.includes(p.code);
                        return (
                          <motion.div
                            key={p.code}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                              const cur = formData.routing_override?.priority_providers || [];
                              const next = cur.includes(p.code) ? cur.filter(x => x !== p.code) : [...cur, p.code];
                              set("routing_override", { ...formData.routing_override, priority_providers: next });
                            }}
                            className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all"
                            style={{
                              background: isPrimary ? "rgba(34,211,238,0.08)" : "rgba(2,6,23,0.4)",
                              borderColor: isPrimary ? "rgba(34,211,238,0.3)" : "rgba(148,163,184,0.1)",
                            }}
                          >
                            <div className="flex-shrink-0">
                              <div className={`w-4 h-4 rounded-md border flex items-center justify-center transition-colors ${isPrimary ? 'bg-cyan-500 border-cyan-500' : 'border-slate-700'}`}>
                                {isPrimary && <CheckCircle2 className="w-3 h-3 text-white" />}
                              </div>
                            </div>
                            <span className={`text-[12px] font-medium truncate ${isPrimary ? 'text-cyan-400' : 'text-slate-400'}`}>
                              {p.code}
                            </span>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* ── API Keys Tab ── */}
          {activeTab === "api" && (
            <motion.div key="api" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.18 }} className="space-y-4">
              <div className="flex gap-3 p-4 rounded-xl"
                style={{ background: "rgba(251,146,60,0.06)", border: "1px solid rgba(251,146,60,0.2)" }}>
                <span className="text-xs text-orange-400">
                  {t("apiKeysAutoGenerated") || "API keys are auto-generated. Keep your secret key confidential."}
                </span>
              </div>

              {[
                { label: t("apiKeyPublic") || "Public API Key", keyVal: formData.api_key, show: showApiKey, onToggle: () => setShowApiKey(v => !v), copyType: "api" },
                { label: t("secretKey") || "Secret Key", keyVal: formData.secret_key, show: showSecretKey, onToggle: () => setShowSecretKey(v => !v), copyType: "secret" },
              ].map(row => (
                <div key={row.copyType}>
                  <FL>{row.label}</FL>
                  <div className="flex gap-2">
                    <div className="flex-1 px-3 py-2.5 rounded-xl font-mono text-xs text-cyan-400 overflow-hidden text-ellipsis whitespace-nowrap"
                      style={{
                        background: "rgba(2,6,23,0.8)",
                        border: `1px solid ${errors.api_key || errors.secret_key ? "rgba(239, 68, 68, 0.4)" : "rgba(34,211,238,0.12)"}`
                      }}>
                      {row.show ? row.keyVal : "••••••••••••••••••••••••••••••••"}
                    </div>
                    {[
                      { icon: row.show ? EyeOff : Eye, fn: row.onToggle, type: "eye" },
                      { icon: copied === row.copyType ? CheckCircle2 : Copy, fn: () => copyToClipboard(row.keyVal, row.copyType), type: "copy" },
                    ].map((btn, i) => (
                      <motion.button key={i} whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.93 }} onClick={btn.fn}
                        className="w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer outline-none flex-shrink-0"
                        style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.12)", transition: "background 0.18s" }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(34,211,238,0.12)"}
                        onMouseLeave={e => e.currentTarget.style.background = "rgba(34,211,238,0.06)"}
                      >
                        <btn.icon className="h-3.5 w-3.5"
                          style={{ color: copied === row.copyType && btn.type === "copy" ? "#34d399" : "#64748b" }} />
                      </motion.button>
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6 pt-4" style={{ borderTop: "1px solid rgba(148,163,184,0.08)" }}>
          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} onClick={() => onClose(false)}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-400 cursor-pointer outline-none border-0"
            style={{ background: "rgba(34,211,238,0.06)", border: "1px solid rgba(34,211,238,0.12)" }}>
            {t("cancel") || "Cancel"}
          </motion.button>
          {!readOnly && (
            <motion.button whileHover={{ scale: 1.03, y: -1 }} whileTap={{ scale: 0.97 }}
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white cursor-pointer outline-none border-0"
              style={{
                background: "linear-gradient(135deg, #22d3ee, #0284c7)",
                boxShadow: "0 4px 16px rgba(34,211,238,0.3)",
                opacity: (saving || !formData.name) ? 0.55 : 1,
                transition: "opacity 0.2s",
              }}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? t("saving") || "Saving…" : isEdit ? t("update") || "Update" : t("create") || "Create"}
            </motion.button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}